import { ReactNode, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertCircle, ChevronDown, Layers, Upload } from 'react-feather';
import AddUserSide from 'assets/icons/AddUserSide';
import ApproveUserSide from 'assets/icons/ApproveUserSide';
import ListUserSide from 'assets/icons/ListUserSide';
import LogSide from 'assets/icons/LogSide';
import EventSide from 'assets/icons/EventSide';
import AdminSide from 'assets/icons/AdminSide';
import { useAuth } from 'core/contexts/auth/AuthContext';
import { UserRoleEnum } from 'core/types/user.type';
import palette from 'core/constant/palette';

/**
 * The admin section's navigation.
 *
 * It used to be a 244px rail and nothing else, at every width. Inside
 * `.container-base` that left 102px for the page on a 390px phone -- so the
 * admin area was not merely cramped on a phone, it was unusable, and the
 * "Quản lý" entry the mobile menu gained in the responsive-shell work led
 * straight to it. Below `lg` the rail is now a disclosure that names the
 * section you are in; from `lg` up it is the rail it always was.
 *
 * Two other things changed while the file was open.
 *
 * The links were raw `<a href>`, so every move between admin screens threw
 * away the React tree and refetched the bundle. They are `<Link>` now.
 *
 * That in turn exposed a latent bug: the icon colours were computed inside a
 * `useMemo` with an empty dependency array, so they were frozen at whatever
 * the first render decided. A full page reload hid it -- every navigation was
 * a first render. With client-side routing the active icon would have kept the
 * previous page's colour, so the menu is plain data now and the colour is
 * resolved at render.
 */

type AdminMenuItem = {
    id: number;
    title: string;
    /** Where the link goes. */
    path: string;
    /** Substring of the pathname that marks this item as the current one. */
    match: string;
    icon: (color: string) => ReactNode;
    roles: UserRoleEnum[];
};

const ALL_ADMINS = [
    UserRoleEnum.PeopleAdmin,
    UserRoleEnum.EditAdmin,
    UserRoleEnum.SiteAdmin,
    UserRoleEnum.SuperAdmin,
];
const EDITORS = [UserRoleEnum.EditAdmin, UserRoleEnum.SiteAdmin, UserRoleEnum.SuperAdmin];
const SUPER_ONLY = [UserRoleEnum.SuperAdmin];

// `match` is the full path for "Thêm người mới" on purpose: three other routes
// end in `/user-detail` and the bare substring would light this item up while
// you were editing somebody from one of the lists.
const NAV_ITEMS: AdminMenuItem[] = [
    {
        id: 1,
        title: 'Thêm người mới',
        path: '/quan-ly/user-detail',
        match: '/quan-ly/user-detail',
        icon: (color) => <AddUserSide fillColor={color} />,
        roles: ALL_ADMINS,
    },
    {
        id: 2,
        title: 'Phê duyệt người mới',
        path: '/quan-ly/pending-user-approval',
        match: '/pending-user-approval',
        icon: (color) => <ApproveUserSide fillColor={color} />,
        roles: EDITORS,
    },
    {
        id: 3,
        title: 'Thay đổi thông tin về người',
        path: '/quan-ly/list-users',
        match: '/list-users',
        icon: (color) => <ListUserSide fillColor={color} />,
        roles: EDITORS,
    },
    {
        id: 4,
        title: 'Quản lý đời',
        path: '/quan-ly/gerenation',
        match: '/gerenation',
        icon: (color) => <Layers size={24} color={color} />,
        roles: SUPER_ONLY,
    },
    {
        id: 5,
        title: 'Lịch sử hoạt động',
        path: '/quan-ly/log',
        match: '/log',
        icon: (color) => <LogSide fillColor={color} />,
        roles: EDITORS,
    },
    {
        id: 6,
        title: 'Quản lý sự kiện',
        path: '/quan-ly/event',
        match: '/event',
        icon: (color) => <EventSide fillColor={color} />,
        roles: EDITORS,
    },
    {
        id: 7,
        title: 'Quản lý admin',
        path: '/quan-ly/admin',
        match: '/admin',
        icon: (color) => <AdminSide fillColor={color} />,
        roles: SUPER_ONLY,
    },
    {
        id: 8,
        title: 'Import người mới',
        path: '/quan-ly/import-user',
        match: '/import-user',
        icon: (color) => <Upload size={23} color={color} />,
        roles: SUPER_ONLY,
    },
    {
        id: 9,
        title: 'Quản lý mật khẩu',
        path: '/quan-ly/ipassword',
        match: '/ipassword',
        icon: (color) => <AlertCircle size={23} color={color} />,
        roles: SUPER_ONLY,
    },
];

const AdminSideBar = () => {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);

    const items = useMemo(
        () => (user?.Role ? NAV_ITEMS.filter((item) => item.roles.includes(user.Role)) : []),
        [user?.Role],
    );

    const current = items.find((item) => pathname.includes(item.match));

    return (
        <nav aria-label="Quản lý" className="bg-gold-soft h-full lg:min-h-[400px]">
            <button
                type="button"
                aria-expanded={open}
                aria-controls="admin-nav-list"
                onClick={() => setOpen((value) => !value)}
                className="lg:hidden w-full flex items-center justify-between gap-3 px-4 py-3.5
                 text-sm font-semibold text-brand text-left
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
            >
                <span className="flex items-center gap-2 min-w-0">
                    <span className="w-6 h-6 shrink-0">
                        {(current ?? NAV_ITEMS[0]).icon(palette.brand)}
                    </span>
                    <span className="truncate">{current?.title ?? 'Menu quản lý'}</span>
                </span>
                <ChevronDown
                    aria-hidden
                    size={20}
                    className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                />
            </button>

            <ul
                id="admin-nav-list"
                className={`${open ? 'block' : 'hidden'} lg:block list-none p-0 m-0
                 border-t border-brand/15 lg:border-t-0`}
            >
                {items.map((item) => {
                    const active = pathname.includes(item.match);
                    return (
                        <li key={item.id}>
                            <Link
                                to={item.path}
                                onClick={() => setOpen(false)}
                                aria-current={active ? 'page' : undefined}
                                className={`flex items-center gap-2 px-4 lg:px-[13px] py-3 text-sm
                                 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand
                                 ${active ? 'bg-brand text-white font-bold' : 'text-brand font-medium hover:bg-gold-pale'}`}
                            >
                                <span className="w-6 h-6 shrink-0">
                                    {item.icon(active ? palette.goldSoft : palette.brand)}
                                </span>
                                <span>{item.title}</span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};

export default AdminSideBar;
